// Stocky CSV 取り込みの1セクション。仕様（CsvImportSpec）で系統ごとの違いを
// 受け取り、進み方は3系統で共通にする:
//   ①ファイル選択 → ②preview で列一覧 → ③列の対応付け（サーバの推測を初期値に）
//   → ④dry_run で上書き件数を確認 → ⑤「上書きして取り込む」で本適用
//
// ④が要るのは、既に手で入れた設定が黙って上書きされ得るため。件数は apply と
// 同じロジックの dry-run から取るので、表示と実際の結果がドリフトしない。
// 🔴 対応付けを変えたら確認済みの件数は破棄する（別の対応付けで数えた件数を
// 確定ボタンの横に残さない）。
import {
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  InlineStack,
  Select,
  Text,
} from "@shopify/polaris";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useRef, useState, type ReactNode } from "react";

import {
  fileToBase64,
  type CsvApplyResultBase,
  type CsvPreview,
} from "../api/stocky_csv_import";
import { t } from "../lib/i18n";
import {
  csvErrorMessage as errorMessage,
  initialMapping,
  isMappingReady,
  toPayloadMapping,
  UNUSED_COLUMN,
  type CsvImportSpec,
} from "./csvImportSpec";

/** 結果バナーに並べる件数の上限（全件並べると読めなくなる）。 */
const MAX_LISTED_SKUS = 10;
const MAX_LISTED_INVALID_ROWS = 10;

interface Props<TResult extends CsvApplyResultBase> {
  spec: CsvImportSpec<TResult>;
  /** 取り込み成功後に呼ばれる（親が関連クエリを invalidate する）。 */
  onImported: () => void;
  /** ファイル選択の直前に置く、その取込経路固有の形式説明や補助操作。 */
  beforeFileInput?: ReactNode;
}

interface ImportSnapshot {
  revision: number;
  fileBase64: string;
  sourceImportId: string;
  sourceFileName: string;
  sourceMediaType: string;
  mapping: ReturnType<typeof toPayloadMapping>;
}

interface ApplyVariables {
  dryRun: boolean;
  snapshot: ImportSnapshot;
  expectedPlanToken?: string;
  confirmRetentionOnly?: boolean;
}

interface PendingConfirmation<TResult extends CsvApplyResultBase> {
  result: TResult;
  snapshot: ImportSnapshot;
}

interface RetentionOnlyContract {
  normalization_mode: "retention_only";
  normalized_rows: 0;
  unprocessed_rows: number;
  domain_normalization_complete: false;
  retention_reason: "hard_row_cap_exceeded";
}

/**
 * retention-only は本当に「正規化0行・未処理=全行」と返った場合だけ
 * 同意 UI へ進める。署名planだけを同意と扱わず、欠落/矛盾応答は fail-closed。
 */
function isValidRetentionOnlyResult(
  result: CsvApplyResultBase,
): result is CsvApplyResultBase & RetentionOnlyContract {
  return (
    result.normalization_mode === "retention_only" &&
    result.normalized_rows === 0 &&
    Number.isSafeInteger(result.unprocessed_rows) &&
    (result.unprocessed_rows ?? -1) >= 0 &&
    result.unprocessed_rows === result.total_rows &&
    result.domain_normalization_complete === false &&
    result.retention_reason === "hard_row_cap_exceeded"
  );
}

function hasValidNormalizationContract(result: CsvApplyResultBase): boolean {
  // 移行中の旧endpointは5項目を返さない。従来のdomain経路として扱う。
  if (result.normalization_mode === undefined) return true;
  if (result.normalization_mode === "retention_only") {
    return isValidRetentionOnlyResult(result);
  }
  return (
    result.normalization_mode === "domain" &&
    Number.isSafeInteger(result.normalized_rows) &&
    (result.normalized_rows ?? -1) >= 0 &&
    result.unprocessed_rows === 0 &&
    result.domain_normalization_complete === true &&
    result.retention_reason === null
  );
}

export function CsvImportPanel<TResult extends CsvApplyResultBase>({
  spec,
  onImported,
  beforeFileInput,
}: Props<TResult>) {
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [fileName, setFileName] = useState("");
  const [sourceImportId, setSourceImportId] = useState("");
  const [sourceMediaType, setSourceMediaType] = useState("text/csv");
  const [fileError, setFileError] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [preview, setPreview] = useState<CsvPreview | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [showMappingDetails, setShowMappingDetails] = useState(false);
  const [result, setResult] = useState<TResult | null>(null);
  /** dry-run の結果。null 以外＝上書き確認の待ち状態。 */
  const [pendingConfirm, setPendingConfirm] =
    useState<PendingConfirmation<TResult> | null>(null);
  const [retentionOnlyConfirmed, setRetentionOnlyConfirmed] = useState(false);
  /**
   * file/mapping が変わるたびに進める世代。非同期応答は開始時の世代と一致した
   * ときだけ採用し、古い dry-run を現在の入力の確認結果として再利用しない。
   */
  const inputRevision = useRef(0);

  const previewMutation = useMutation({
    mutationFn: (variables: { fileBase64: string; revision: number }) =>
      spec.preview(variables.fileBase64),
    onMutate: () => setPreviewError(null),
    onSuccess: (data, variables) => {
      if (variables.revision !== inputRevision.current) return;
      const nextMapping = initialMapping(spec, data.guessed_mapping);
      setPreview(data);
      setResult(null);
      setPendingConfirm(null);
      setRetentionOnlyConfirmed(false);
      setMapping(nextMapping);
      setShowMappingDetails(
        spec.autoMapKnownColumns === true
          ? !isMappingReady(spec, nextMapping)
          : true,
      );
    },
    onError: (error, variables) => {
      if (variables.revision !== inputRevision.current) return;
      setPreviewError(errorMessage(error));
    },
  });

  const applyMutation = useMutation({
    mutationFn: ({
      dryRun,
      snapshot,
      expectedPlanToken,
      confirmRetentionOnly,
    }: ApplyVariables) =>
      spec.apply(snapshot.fileBase64, snapshot.mapping, {
        dryRun,
        expectedPlanToken,
        confirmRetentionOnly,
        sourceImportId: snapshot.sourceImportId,
        sourceFileName: snapshot.sourceFileName,
        sourceMediaType: snapshot.sourceMediaType,
      }),
    onMutate: () => setApplyError(null),
    onSuccess: (data, variables) => {
      if (variables.snapshot.revision !== inputRevision.current) return;
      if (data.dry_run !== variables.dryRun) {
        setPendingConfirm(null);
        setRetentionOnlyConfirmed(false);
        setApplyError(
          t(
            "確認状態が一致しません。CSV と列の対応付けを確認して、もう一度お試しください。",
          ),
        );
        return;
      }
      if (!hasValidNormalizationContract(data)) {
        setPendingConfirm(null);
        setRetentionOnlyConfirmed(false);
        setApplyError(
          t(
            "確認状態が一致しません。CSV と列の対応付けを確認して、もう一度お試しください。",
          ),
        );
        return;
      }
      if (
        !variables.dryRun &&
        data.normalization_mode === "retention_only" &&
        variables.confirmRetentionOnly !== true
      ) {
        setPendingConfirm(null);
        setRetentionOnlyConfirmed(false);
        setApplyError(
          t(
            "確認状態が一致しません。CSV と列の対応付けを確認して、もう一度お試しください。",
          ),
        );
        return;
      }
      if (variables.dryRun) {
        setRetentionOnlyConfirmed(false);
        setPendingConfirm({ result: data, snapshot: variables.snapshot });
        return;
      }
      setPendingConfirm(null);
      setRetentionOnlyConfirmed(false);
      setResult(data);
      if (
        data.normalization_mode !== "retention_only" &&
        spec.writtenCount(data) > 0
      ) {
        onImported();
      }
    },
    onError: (error, variables) => {
      if (variables.snapshot.revision !== inputRevision.current) return;
      setApplyError(errorMessage(error));
    },
  });

  /** 対応付けを変えたら確認済みの件数は無効にする。 */
  const updateMapping = useCallback((key: string, value: string) => {
    inputRevision.current += 1;
    // 同じbytesでも列mappingが変われば別の解釈 occurrence。
    setSourceImportId(globalThis.crypto.randomUUID());
    setPendingConfirm(null);
    setRetentionOnlyConfirmed(false);
    setResult(null);
    setApplyError(null);
    setMapping((current) => ({ ...current, [key]: value }));
  }, []);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const revision = inputRevision.current + 1;
      inputRevision.current = revision;
      setFileName(file.name);
      // 1回の選択が1つの source occurrence。dry-run→apply→通信再試行では
      // snapshot内の同じUUIDを使い、同じファイルを選び直した場合も新規発行する。
      setSourceImportId(globalThis.crypto.randomUUID());
      setSourceMediaType(file.type || "text/csv");
      setFileBase64(null);
      setPreview(null);
      setMapping({});
      setShowMappingDetails(false);
      setResult(null);
      setPendingConfirm(null);
      setRetentionOnlyConfirmed(false);
      setFileError(null);
      setPreviewError(null);
      setApplyError(null);
      try {
        const base64 = await fileToBase64(file);
        if (revision !== inputRevision.current) return;
        setFileBase64(base64);
        previewMutation.mutate({ fileBase64: base64, revision });
      } catch {
        if (revision !== inputRevision.current) return;
        setFileBase64(null);
        setPreview(null);
        setFileError(
          t(
            "ファイルを読み込めませんでした。CSV ファイルを選び直してください。",
          ),
        );
      }
    },
    [previewMutation],
  );

  const isConfirming =
    pendingConfirm !== null &&
    pendingConfirm.snapshot.revision === inputRevision.current;
  const pendingRetentionResult =
    isConfirming && isValidRetentionOnlyResult(pendingConfirm.result)
      ? pendingConfirm.result
      : null;
  const isBusy = previewMutation.isPending || applyMutation.isPending;
  const canApply =
    preview !== null &&
    fileBase64 !== null &&
    isMappingReady(spec, mapping) &&
    !isBusy;
  const mappingReady = isMappingReady(spec, mapping);
  const mappingWasAutoRecognized =
    spec.autoMapKnownColumns === true && mappingReady;
  const isDestructive =
    isConfirming && spec.overwriteCount(pendingConfirm.result) > 0;

  const startApply = useCallback(() => {
    if (isBusy) return;
    if (pendingConfirm !== null) {
      if (pendingConfirm.snapshot.revision !== inputRevision.current) {
        setPendingConfirm(null);
        setRetentionOnlyConfirmed(false);
        return;
      }
      if (
        pendingConfirm.result.normalization_mode === "retention_only" &&
        !retentionOnlyConfirmed
      ) {
        return;
      }
      applyMutation.mutate({
        dryRun: false,
        snapshot: pendingConfirm.snapshot,
        expectedPlanToken: pendingConfirm.result.plan_token ?? undefined,
        confirmRetentionOnly:
          pendingConfirm.result.normalization_mode === "retention_only",
      });
      return;
    }
    if (!fileBase64 || !sourceImportId || !isMappingReady(spec, mapping))
      return;
    applyMutation.mutate({
      dryRun: true,
      snapshot: {
        revision: inputRevision.current,
        fileBase64,
        sourceImportId,
        sourceFileName: fileName,
        sourceMediaType,
        mapping: toPayloadMapping(spec, mapping),
      },
    });
  }, [
    applyMutation,
    fileBase64,
    fileName,
    isBusy,
    mapping,
    pendingConfirm,
    retentionOnlyConfirmed,
    sourceImportId,
    sourceMediaType,
    spec,
  ]);

  const title = t(spec.title);
  const actionLabel =
    pendingRetentionResult !== null
      ? t("原本と汎用JSONLを保存する")
      : isConfirming
        ? t("上書きして取り込む")
        : t("取り込む");

  return (
    <section aria-label={title}>
      <Card>
        <BlockStack gap="400">
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">
              {title}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {t(spec.description)}
            </Text>
            {spec.exportPath && (
              <Text as="p" variant="bodySm" tone="subdued">
                {t("書き出し方")}: {t(spec.exportPath)}
              </Text>
            )}
          </BlockStack>

          {beforeFileInput}
          {spec.expectedFileName && (
            <Text as="p" fontWeight="semibold">
              {t("選ぶファイル: {name}", {
                name: t(spec.expectedFileName),
              })}
            </Text>
          )}
          <input
            type="file"
            accept=".csv,text/csv"
            id={`${spec.id}-file`}
            aria-label={t("{title}のCSVファイル", { title })}
            disabled={isBusy}
            onChange={(event) => void handleFileChange(event)}
          />
          {fileName && (
            <Text as="p" variant="bodySm" tone="subdued">
              {t("選択中: {name}", { name: fileName })}
            </Text>
          )}

          {fileError && (
            <Banner tone="critical" title={t("ファイルを読み込めませんでした")}>
              <p>{fileError}</p>
            </Banner>
          )}
          {previewError && (
            <Banner tone="critical" title={t("CSV を読み込めませんでした")}>
              <p>{previewError}</p>
            </Banner>
          )}

          {preview && (
            <BlockStack gap="300">
              <Text as="p">
                {mappingWasAutoRecognized
                  ? t(
                      "{rows} 行を検出し、Stockyの列を自動認識しました。列を選び直さず、そのまま件数確認へ進めます。",
                      { rows: preview.total_rows },
                    )
                  : t(
                      "{rows} 行を検出しました。自動認識できない列だけ、詳細設定で選んでください。",
                      { rows: preview.total_rows },
                    )}
              </Text>
              {mappingWasAutoRecognized && (
                <InlineStack align="start">
                  <Button
                    onClick={() => setShowMappingDetails((open) => !open)}
                    ariaExpanded={showMappingDetails}
                  >
                    {showMappingDetails
                      ? t("列の詳細設定を閉じる")
                      : t("列の詳細設定（通常は変更不要）")}
                  </Button>
                </InlineStack>
              )}
              {showMappingDetails && (
                <MappingSelects
                  spec={spec}
                  headers={preview.headers}
                  mapping={mapping}
                  onChange={updateMapping}
                  disabled={isBusy}
                />
              )}
              {!isMappingReady(spec, mapping) && (
                <Text as="p" variant="bodySm" tone="subdued">
                  {t(spec.readyHint)}
                </Text>
              )}
              <InlineStack align="end">
                <Button
                  variant="primary"
                  tone={isDestructive ? "critical" : undefined}
                  disabled={
                    !canApply ||
                    (pendingRetentionResult !== null && !retentionOnlyConfirmed)
                  }
                  loading={applyMutation.isPending}
                  onClick={startApply}
                  // 同じ文言のボタンが3セクションぶん並ぶ＝読み上げでは区別できない。
                  accessibilityLabel={t("{title}のCSVを{action}", {
                    title,
                    action: actionLabel,
                  })}
                >
                  {actionLabel}
                </Button>
              </InlineStack>
            </BlockStack>
          )}

          {isConfirming && pendingRetentionResult === null && (
            <ConfirmBanner spec={spec} result={pendingConfirm.result} />
          )}

          {pendingRetentionResult !== null && (
            <RetentionConfirmBanner
              result={pendingRetentionResult}
              checked={retentionOnlyConfirmed}
              disabled={isBusy}
              onChange={setRetentionOnlyConfirmed}
            />
          )}

          {applyError && (
            <Banner tone="critical" title={t("取り込みに失敗しました")}>
              <p>{applyError}</p>
            </Banner>
          )}

          {result && isValidRetentionOnlyResult(result) ? (
            <RetentionResultBanner result={result} />
          ) : (
            result && <ResultBanner spec={spec} result={result} />
          )}
        </BlockStack>
      </Card>
    </section>
  );
}

interface MappingSelectsProps<TResult extends CsvApplyResultBase> {
  spec: CsvImportSpec<TResult>;
  headers: string[];
  mapping: Record<string, string>;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
}

/** 列の対応付け Select 群。棚卸し履歴の一括取り込みも同じ見た目で借りる。 */
export function MappingSelects<TResult extends CsvApplyResultBase>({
  spec,
  headers,
  mapping,
  onChange,
  disabled = false,
}: MappingSelectsProps<TResult>) {
  // 列名は CSV そのままの見出し＝訳さない（マーチャントのファイルの実物）。
  const options = [
    { label: t("（使わない）"), value: UNUSED_COLUMN },
    ...headers.map((header) => ({ label: header, value: header })),
  ];

  return (
    <BlockStack gap="300">
      {spec.fields.map((field) => (
        <Select
          key={field.key}
          id={`${spec.id}-${field.key}`}
          label={t(field.label)}
          options={options}
          value={mapping[field.key] ?? UNUSED_COLUMN}
          onChange={(value) => onChange(field.key, value)}
          disabled={disabled}
        />
      ))}
    </BlockStack>
  );
}

interface BannerProps<TResult extends CsvApplyResultBase> {
  spec: CsvImportSpec<TResult>;
  result: TResult;
}

function NormalizationCounts({ result }: { result: CsvApplyResultBase }) {
  if (
    !Number.isSafeInteger(result.normalized_rows) ||
    !Number.isSafeInteger(result.unprocessed_rows)
  ) {
    return null;
  }
  return (
    <Text as="p">
      {t("運用データへ正規化済み {normalized} 行・未処理 {unprocessed} 行。", {
        normalized: (result.normalized_rows ?? 0).toLocaleString("en-US"),
        unprocessed: (result.unprocessed_rows ?? 0).toLocaleString("en-US"),
      })}
    </Text>
  );
}

interface RetentionConfirmBannerProps {
  result: CsvApplyResultBase & RetentionOnlyContract;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}

/** 100万行超は運用変換せず、別操作の明示同意後に原本と汎用行を保存する。 */
function RetentionConfirmBanner({
  result,
  checked,
  disabled,
  onChange,
}: RetentionConfirmBannerProps) {
  return (
    <Banner
      tone="warning"
      title={t(
        "まだ保存していません。運用データへ書かない保全内容を確認してください",
      )}
    >
      <BlockStack gap="200">
        <Text as="p">
          {t(
            "運用データの安全上限により、この取込全体は運用モデルへ1件も変換せず、送信された正確な原本と解析可能な全行を汎用JSONLとして保持します（{rows}行すべて運用変換は未処理）。",
            { rows: result.unprocessed_rows.toLocaleString("en-US") },
          )}
        </Text>
        <NormalizationCounts result={result} />
        {/* 上の exact contract（0/total/false/reason）を確認できた場合だけ断定する。 */}
        <Text as="p">{t("運用データへの書き込みは0件です。")}</Text>
        <Text as="p">
          {t(
            "保存後は、この画面の「保存済みデータの取り出し」から原本をダウンロードし、解析可能な全行の汎用JSONLを分割取得できます。",
          )}
        </Text>
        <Checkbox
          label={t(
            "運用モデルへ書き込まず、正確な原本と解析可能な全行を汎用JSONLとして保全することを確認する",
          )}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
        />
      </BlockStack>
    </Banner>
  );
}

function RetentionResultBanner({
  result,
}: {
  result: CsvApplyResultBase & RetentionOnlyContract;
}) {
  return (
    <Banner
      tone="warning"
      title={t("原本と汎用JSONLを保存しました（運用データは未反映）")}
    >
      <BlockStack gap="200">
        <NormalizationCounts result={result} />
        <Text as="p">{t("運用データへの書き込みは0件です。")}</Text>
        <Text as="p">
          {t(
            "正確な原本と解析可能な全行を汎用JSONLとして保存しました。この画面の「保存済みデータの取り出し」から原本をダウンロードし、汎用JSONLを分割取得できます。",
          )}
        </Text>
      </BlockStack>
    </Banner>
  );
}

/** dry-run の結果＝まだ何も保存していないことを明示する確認バナー。 */
function ConfirmBanner<TResult extends CsvApplyResultBase>({
  spec,
  result,
}: BannerProps<TResult>) {
  const overwrite = spec.overwriteCount(result);
  const unmatched = spec.unmatchedSkus(result);
  const noun = t(spec.overwriteNoun);

  return (
    <Banner
      tone={overwrite > 0 ? "warning" : "info"}
      title={
        overwrite > 0
          ? t("既存の{noun} {count} 件を上書きします", {
              noun,
              count: overwrite,
            })
          : t("上書きされる既存の{noun}はありません", { noun })
      }
    >
      <BlockStack gap="200">
        <Text as="p">
          {t(
            t(
              "{summary}。よろしければ「上書きして取り込む」を押してください（まだ何も保存していません）。",
            ),
            { summary: spec.summarize(result) },
          )}
        </Text>
        <NormalizationCounts result={result} />
        {overwrite > 0 && (
          <Text as="p">
            {t(
              "上書きされるのは対応付けた列だけです（空欄のセル・「（使わない）」にした列の既存値は残ります）。",
            )}
          </Text>
        )}
        {/* 🔴 件数は真値（*_count）＝配列は表示用に切り詰め済み。 */}
        {(result.unmatched_count ?? unmatched.length) > 0 && (
          <Text as="p">
            {t(
              "一致する商品が見つからない SKU: {count} 件（この行は取り込まれません）。",
              {
                count: result.unmatched_count ?? unmatched.length,
              },
            )}
          </Text>
        )}
        {(result.invalid_row_count ?? result.invalid_rows.length) > 0 && (
          <Text as="p">
            {t("読み飛ばす行: {count} 件。", {
              count: result.invalid_row_count ?? result.invalid_rows.length,
            })}
          </Text>
        )}
        {spec.diagnostics?.(result).map(
          (diagnostic) =>
            diagnostic.count > 0 && (
              <Text as="p" key={diagnostic.confirmTemplate}>
                {t(diagnostic.confirmTemplate, { count: diagnostic.count })}
              </Text>
            ),
        )}
      </BlockStack>
    </Banner>
  );
}

/** 上限まで並べた一覧（打ち切ったぶんは末尾の … で示す）。 */
function joinListed(
  items: readonly string[],
  limit: number,
  separator: string,
): string {
  const shown = items.slice(0, limit);
  return shown.join(separator) + (items.length > shown.length ? " …" : "");
}

/** 本適用の結果。読み飛ばした行と一致しなかった SKU を必ず出す。 */
function ResultBanner<TResult extends CsvApplyResultBase>({
  spec,
  result,
}: BannerProps<TResult>) {
  const unmatched = spec.unmatchedSkus(result);
  // reason は backend が request locale で返す完成文＝再翻訳せずそのまま差し込む。
  const rows = result.invalid_rows.map((row) =>
    t("{row}行目（{reason}）", { row: row.row, reason: row.reason }),
  );

  return (
    <Banner
      tone={spec.writtenCount(result) > 0 ? "success" : "warning"}
      title={spec.resultTitle(result)}
    >
      <BlockStack gap="200">
        <Text as="p">{spec.summarize(result)}</Text>
        <NormalizationCounts result={result} />
        {(result.unmatched_count ?? unmatched.length) > 0 && (
          <Text as="p">
            {t(
              t(
                "一致する商品が見つからなかった SKU（{count} 件）: {skus}。先に在庫同期を実行してから再取り込みしてください。",
              ),
              {
                count: result.unmatched_count ?? unmatched.length,
                skus: joinListed(unmatched, MAX_LISTED_SKUS, ", "),
              },
            )}
          </Text>
        )}
        {(result.invalid_row_count ?? rows.length) > 0 && (
          <Text as="p">
            {t("読み飛ばした行（{count} 件）: {rows}", {
              count: result.invalid_row_count ?? rows.length,
              rows: joinListed(rows, MAX_LISTED_INVALID_ROWS, " / "),
            })}
          </Text>
        )}
        {spec.diagnostics?.(result).map(
          (diagnostic) =>
            diagnostic.count > 0 && (
              <Text as="p" key={diagnostic.resultTemplate}>
                {t(diagnostic.resultTemplate, {
                  count: diagnostic.count,
                  values: joinListed(diagnostic.items, MAX_LISTED_SKUS, ", "),
                })}
              </Text>
            ),
        )}
      </BlockStack>
    </Banner>
  );
}
